import LicenceProfileHeader from "../LicenceProfileHeader";
import LicenceProfileSubTabs from "../LicenceProfileSubTabs";
import BreadCrumbs from "./BreadCrumbs";
import { Link, useParams } from "react-router-dom";
import Page404 from "../../../pages/auth/Page404";
import useAppSelector from "../../../hooks/useAppSelector";
import {
  loadLicenceProfileAsync,
  selectLicenceProfileResponse,
} from "../../../redux/slices/licenceProfile";
import Loading from "../../../leap-components/Loading";
import useAppDispatch from "../../../hooks/useAppDispatch";
import { ICompliancePropsWithFilters } from "../../../leap-components/Compliance/IComplianceProps";
import { Column } from "react-table";
import {
  GetComplianceListFetchUrl,
  ICompliance,
} from "../../../api/ICompliance";
import { getIconForType } from "../../../leap-components/icons/LeapIcons";
import EpaCard from "../../../leap-components/EpaCard";
import { Grid } from "../../../leap-components/Grid";
import { formatDate } from "../../../api/DateUtils";
import { CreateStaticSelectColumnFilter } from "../../../leap-components/Grid/FetchGrid/StaticSelectColumnFilter";
import {
  loadComplianceOptionsAsync,
  selectComplianceOptions,
} from "../../../redux/slices/compliance";
import { DateRangeColumnFilter } from "../../../leap-components/Grid/FetchGrid/DateRangeColumnFilter";
import { useState } from "react";
import {
  GetLicenceProfileNotReadyComponent,
  IsLicenceProfileReady,
} from "../../../leap-components/LicenceProfile/LicenceProfileResponseComponentFactory";
import { ILicenceProfile } from "../../../api/LicenceProfile";
import { GetComplianceRecordDetailsUrl } from "../../routeFactory";

const ComplianceServerSide = ({
  licenceProfile,
  complianceRecordTypes,
  complianceStatuses,
}: ICompliancePropsWithFilters) => {
  const tableColumnsBig: Column<Record<string, ICompliance>>[] = [
    {
      Header: "Category",
      accessor: "type",
      Filter: CreateStaticSelectColumnFilter(
        complianceRecordTypes.list.map((option) => option.type)
      ),
      filter: "includes",
      Cell: (rowInfo: any) => (
        <table>
          <tr>
            <td style={{ paddingTop: "6px" }}>
              <span>{getIconForType(rowInfo.value)}</span>
            </td>
            <td>{rowInfo.value}</td>
          </tr>
        </table>
      ),
    },
    {
      Header: "Ref. No.",
      accessor: "reference",
      Filter: false,
      Cell: (rowInfo: any) => {
        const rowData = rowInfo.data[rowInfo.row.index];
        return (
          <Link
            to={GetComplianceRecordDetailsUrl(
              rowData.compliancerecord_id,
              rowData.type,
              licenceProfile.profileNo
            )}
          >
            {rowInfo.value}
          </Link>
        );
      },
    },
    {
      Header: "Subject",
      accessor: "subject",
      Filter: false,
    },
    {
      Header: "Date",
      accessor: "date",
      Filter: DateRangeColumnFilter,
      filter: "includes",
      Cell: (rowInfo: any) => formatDate(rowInfo.value),
    },
    {
      Header: "Status",
      accessor: "status",
      Filter: CreateStaticSelectColumnFilter(
        complianceStatuses.list.map((option) => option.status)
      ),
      filter: "includes",
    },
    {
      Header: "Authorisation Version",
      accessor: "licenceregno",
      Filter: CreateStaticSelectColumnFilter(
        licenceProfile.licenceVersionsList.list.map((version) => version.reg_no)
      ),
      filter: "includes",
    },
  ];

  const tableColumnsSmall: Column<Record<string, ICompliance>>[] = [
    {
      Header: "Category",
      accessor: "type",
      Filter: CreateStaticSelectColumnFilter(
        complianceRecordTypes.list.map((option) => option.type)
      ),
      filter: "includes",
      Cell: (rowInfo: any) => (
        <table>
          <tr>
            <td style={{ paddingTop: "6px" }}>
              <span>{getIconForType(rowInfo.value)}</span>
            </td>
            <td>{rowInfo.value}</td>
          </tr>
        </table>
      ),
    },
    {
      Header: "Ref. No.",
      accessor: "reference",
      Filter: false,
      Cell: (rowInfo: any) => {
        const rowData = rowInfo.data[rowInfo.row.index];
        return (
          <Link
            to={GetComplianceRecordDetailsUrl(
              rowData.compliancerecord_id,
              rowData.type,
              licenceProfile.profileNo
            )}
          >
            {rowInfo.value}
          </Link>
        );
      },
    },
    {
      Header: "Subject",
      accessor: "subject",
      Filter: false,
    },
  ];

  const fetchUrl = (
    pageNo: number,
    recordsPerPage: number,
    searchText: string,
    filters: any
  ) => {
    return GetComplianceListFetchUrl(
      licenceProfile.id,
      pageNo,
      recordsPerPage,
      searchText,
      filters
    );
  };

  var bodyElements = (
    <div>
      <div className="d-md-none">
        <Grid
          columns={tableColumnsSmall}
          data={[]}
          fetchUrlFunction={fetchUrl}
          enablePagination={true}
          enableQuickFind={true}
        />
      </div>
      <div className="d-none d-md-block">
        <Grid
          columns={tableColumnsBig}
          data={[]}
          fetchUrlFunction={fetchUrl}
          enablePagination={true}
          enableQuickFind={true}
        />
      </div>
    </div>
  );

  return <EpaCard title={""} content={bodyElements} />;
};

const ComplianceTab = () => {
  const [loading, setLoading] = useState(false);
  const licenceProfileSelector = useAppSelector(selectLicenceProfileResponse);
  const complianceOptionsSelector = useAppSelector(selectComplianceOptions);
  const dispatch = useAppDispatch();

  let { profileNo } = useParams();
  if (!profileNo) {
    return <Page404 />;
  }

  if (!loading) {
    setLoading(true);
    dispatch(loadLicenceProfileAsync(profileNo));
  }

  if (!IsLicenceProfileReady(licenceProfileSelector.licenceProfileResponse)) {
    return GetLicenceProfileNotReadyComponent(
      licenceProfileSelector.licenceProfileResponse
    );
  }

  const licenceProfile = licenceProfileSelector.licenceProfileResponse
    ?.licenceProfile as ILicenceProfile;

  if (
    !complianceOptionsSelector.complianceRecordTypes ||
    !complianceOptionsSelector.complianceStatuses
  ) {
    dispatch(loadComplianceOptionsAsync(licenceProfile.id));
    return <Loading />;
  }

  const contentElement = (
    <ComplianceServerSide
      licenceProfile={licenceProfile}
      complianceRecordTypes={complianceOptionsSelector.complianceRecordTypes}
      complianceStatuses={complianceOptionsSelector.complianceStatuses}
    />
  );

  return (
    <div>
      <BreadCrumbs licenceProfile={licenceProfile} />
      <div className="ps-0">
        <LicenceProfileHeader licenceProfile={licenceProfile} />
        <LicenceProfileSubTabs
          activeKey="compliance-records"
          content={contentElement}
          licenceProfile={licenceProfile}
        />
      </div>
    </div>
  );
};

export default ComplianceTab;
